import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./ChooseNiche.css";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

 

export default function ChooseNiche() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [navigating, setNavigating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const userStatus = localStorage.getItem("user_status");
    const nicheCode = userStatus === "salon" ? "226" : "214";
    localStorage.setItem("onboarding_code", nicheCode);

    sessionStorage.setItem("onboarding_force_route", "/choose-niche");
    sessionStorage.setItem("onboarding_force_status", userStatus || "individual");
  }, []);

  const getValidToken = async () => {
    let token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      "";

    if (!token) return null;

    const headers = buildHeaders();

    const verifyRes = await fetch(`${BASE_URL}/auth/token/verify/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ token }),
    });

    if (verifyRes.ok) return token;

    const refresh =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");

    if (!refresh) return null;

    const refreshRes = await fetch(`${BASE_URL}/auth/token/refresh/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ refresh }),
    });

    const data = await refreshRes.json().catch(() => ({}));

    if (refreshRes.ok && data.access) {
      const useLocalStorage =
        !!localStorage.getItem("accessToken") ||
        !!localStorage.getItem("refreshToken");

      if (useLocalStorage) {
        localStorage.setItem("accessToken", data.access);
      } else {
        sessionStorage.setItem("accessToken", data.access);
      }

      return data.access;
    }

    return null;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError("");

        const token = await getValidToken();

        if (!token) {
          if (isMounted) {
            setError(t("auth.errorSessionExpired"));
            setLoading(false);
          }
          return;
        }

        const headers = buildHeaders();

        const res = await fetch(`${BASE_URL}/auth/profession-categories/`, {
          method: "GET",
          headers,
        });

        const data = await res.json().catch(() => []);

        if (!res.ok) {
          throw new Error(data?.detail || t("auth.errorFetchCategories"));
        }

        const formatted = Array.isArray(data)
          ? data.map((item) => {
              let imageUrl = item.icon;

              if (imageUrl && !/^https?:\/\//i.test(imageUrl)) {
                imageUrl = `${BASE_URL}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;
              }

              return {
                id: item.id,
                name: item.title || "Untitled",
                title: item.title || "Untitled",
                image_url: imageUrl || "/placeholder.jpg",
                icon: imageUrl || "/placeholder.jpg",
                raw: item,
              };
            })
          : [];

        if (isMounted) {
          setCategories(formatted);
        }
      } catch (err) {
        console.error("Failed to load business niches:", err);
        if (isMounted) {
          setError(t("auth.errorLoadNiches"));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelect = async (cat) => {
    if (navigating) return;

    setSelected(cat);
    setError("");
    setNavigating(true);

    try {
      const token = await getValidToken();

      if (!token) {
        setError(t("auth.errorSessionExpired"));
        setNavigating(false);
        return;
      }

      const userTimeZone = getUserTimeZone();
      const userStatus = localStorage.getItem("user_status") || "individual";

      localStorage.setItem("selectedNiche", JSON.stringify(cat));
      localStorage.setItem("selectedNicheId", String(cat.id));
      localStorage.setItem("selectedNicheName", cat.name || cat.title || "");

      if (cat.id === 34 || cat.name === "Sales & Real Estate") {
        localStorage.setItem("real_estate_mode", "true");
      } else {
        localStorage.removeItem("real_estate_mode");
      }

      sessionStorage.setItem("onboarding_force_route", "/subniche-select");
      sessionStorage.setItem("onboarding_force_status", userStatus);

      navigate("/subniche-select", {
        replace: true,
        state: {
          niche: cat,
          timeZone: userTimeZone,
        },
      });
    } catch (err) {
      console.error("Failed while selecting niche:", err);
      setError(t("auth.errorContinue"));
      setNavigating(false);
    }
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (
        e.key === "Enter" &&
        !selected &&
        categories.length > 0 &&
        !loading &&
        !navigating
      ) {
        e.preventDefault();
        handleSelect(categories[0]);
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [selected, categories, loading, navigating]);

  return (
    <div className="niche-onboarding">
      <main className="niche-onboarding-main">
        <motion.div
          className="niche-onboarding-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="niche-onboarding-title">
            {t("auth.nicheTitle")}
          </h2>

          <p className="niche-onboarding-subtitle">
            {t("auth.nicheSubtitle")}
          </p>

          {error && <p className="niche-onboarding-error">{error}</p>}

          {loading || navigating ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="niche-loading flex flex-col items-center justify-center min-h-[240px] w-full"
            >
              <div className="w-full flex items-center justify-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.2,
                    ease: "linear",
                  }}
                  className="w-12 h-12 border-4 border-[#020637] border-t-transparent rounded-full mb-3 flex items-center justify-center"
                />
              </div>

              <p className="text-[#020637] text-sm font-medium">
                {loading ? t("auth.nicheLoading") : t("auth.nicheNavigating")}
              </p>
            </motion.div>
          ) : (
            <div className="niche-onboarding-grid">
              {categories.length > 0 ? (
                categories.map((cat) => (
                  <motion.div
                    key={cat.id}
                    className={`niche-onboarding-item ${
                      selected?.id === cat.id ? "active" : ""
                    }`}
                    onClick={() => handleSelect(cat)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={cat.image_url}
                      alt={cat.name}
                      className="niche-onboarding-image"
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder.jpg";
                      }}
                      loading="lazy"
                    />
                    <div className="niche-onboarding-overlay">
                      <span className="niche-onboarding-name">{cat.name}</span>
                    </div>
                  </motion.div>
                ))
              ) : (
                <motion.p
                  className="niche-onboarding-empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {t("auth.nicheEmpty")}
                </motion.p>
              )}
            </div>
          )}

          <div className="niche-onboarding-step">{t("auth.nicheStep")}</div>
        </motion.div>
      </main>
    </div>
  );
}